import { useState, useCallback, useEffect } from "react";
import { UserTeamInfo } from "JS/Models";
import { useSnackbar } from "JS/Helpers/Snackbar";
import { useAppDispatch, useAppSelector } from "JS/Redux/Store";
import { HomeService } from "JS/Services/Home";
import { useGlobalMediaEssentials } from "./MediaEssentials";
import { setUserTeamInfo, setLandingPageJson } from "JS/Redux/Home";
import { messaging } from "JS/Helpers/UserMessaging";
import { useAuth } from "../Context/AuthenticationProvider";
import { config } from "JS/Config";
import { useRouting } from "./Routes";
import { useNavigate } from "react-router-dom";

const service = new HomeService();
export const useTeamInformation = (skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const [landingPageJsonloading, setLandingPageJsonLoading] = useState(false);
    const [teamInfo, setTeamInfo] = useState<UserTeamInfo>(null);
    const { mediaEssentials } = useGlobalMediaEssentials();

    const dispatch = useAppDispatch();

    const { enqueueSnackbar } = useSnackbar();

    const userTeamInformation = useAppSelector(
        (state) => state.home.userTeamInformation,
    );
    const landingPageJson = useAppSelector(
        (state) => state.home.landingPageJson,
    );

    const baseHomePagePath = "LandingPage";
    const homePageFileName = "landing_page.json";
    const testHomePageFileName = "test_landing_page.json";
    const getTeamHomePagePath = (teamName: string) =>
        `${baseHomePagePath}/${teamName}/${homePageFileName}`;
    const getTestUserHomePagePath = (teamName: string) =>
        `${baseHomePagePath}/${teamName}/${testHomePageFileName}`;

    const refetch = useCallback(() => {
        setLoading(true);
        const fetchJsonFileForHomePage = (filePath: string) => {
            setLandingPageJsonLoading(true);
            service
                .getLandingPageJson(filePath)
                .then((response) => {
                    setLandingPageJsonLoading(false);
                    dispatch(
                        setLandingPageJson({
                            landingPageJson: response,
                        }),
                    );
                })
                .catch((err) => {
                    setLandingPageJsonLoading(false);
                    console.error("Error fetching landing page JSON:", err);
                });
        };
        return service
            .getTeamsStatusForUser(
                mediaEssentials?.allow_fetch_team_status,
                userTeamInformation,
            )
            .then(async (response) => {
                const teamInfoData = response?.data;
                fetchJsonFileForHomePage(
                    teamInfoData?.testUser
                        ? getTestUserHomePagePath(teamInfoData?.teamName)
                        : getTeamHomePagePath(teamInfoData?.teamName),
                );
                setTeamInfo(teamInfoData);
                dispatch(
                    setUserTeamInfo({
                        userTeamInformation: teamInfoData,
                    }),
                );

                return response;
            })
            .catch((err) => {
                enqueueSnackbar(err.message, {
                    variant: "error",
                });
                return {
                    data: [],
                    status: false,
                    message: err.message,
                };
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip) refetch();
    }, [skip]);

    return {
        refetch,
        teamInfo,
        loading,
        userTeamInformation,
        landingPageJsonloading,
        landingPageJson,
    };
};

export const useExternalSso = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { handleSuccessfulLogin } = useAuth();
    const navigate = useNavigate();
    const { linkProvider } = useRouting();
    const redirectToHome = () => navigate(linkProvider.react.home(), { replace: true });

    const fetch = (accessToken: string, memberId: string) => {
        if (accessToken && memberId) {
            if (!!config?.user?.memberId && memberId !== config.user.memberId) {
                enqueueSnackbar(messaging.sso.invalidUserError(memberId), {
                    variant: "error",
                });
                redirectToHome();
            } else
                service
                    .getExternalSso(accessToken, memberId)
                    .then(async (r) => {
                        const user = r.data;
                        if (!user) throw new Error();
                        await handleSuccessfulLogin({ ...user, memberId });
                    })
                    .catch(() =>
                        enqueueSnackbar(messaging.common.error, {
                            variant: "error",
                        }),
                    )
                    .finally(redirectToHome);
        } else
            enqueueSnackbar(messaging.sso.missingParamsError, {
                variant: "error",
            });
    };

    return {
        fetch,
    };
};
